import { PROJECTS_TABLE_SEARCH_DEBOUNCE_MS } from '@/constants/projects.constants';
import { useProjectContext } from '@/context/ProjectContext';
import { useEnsureSelectedProjectInList } from '@/hooks/projects/useEnsureSelectedProjectInList';
import { useProjectDropdownOptions } from '@/hooks/projects/useProjectDropdownOptions';
import type { Project } from '@/interfaces/projects.interface';
import { Dropdown } from 'primereact/dropdown';
import { useDebounce } from 'primereact/hooks';
import { useCallback, useEffect, useMemo, useRef } from 'react';

interface ProjectDropdownProps {
  size?: 'small' | 'medium';
  showClear?: boolean;
  /** Accessible label announced by screen readers. */
  ariaLabel?: string;
}

export const ProjectDropdown = ({
  size = 'small',
  showClear = false,
  ariaLabel = 'Select a project',
}: ProjectDropdownProps) => {
  const [, debouncedFilter, setFilterInput] = useDebounce(
    '',
    PROJECTS_TABLE_SEARCH_DEBOUNCE_MS
  );
  const hasOpenedRef = useRef(false);
  const firstPageRequestedRef = useRef(false);
  const prevDebouncedFilterRef = useRef<string | undefined>(undefined);
  const didMountPrefetchRef = useRef(false);

  const {
    selectedProjectId,
    setSelectedProjectId,
    setProjectSelectorVisible,
    dropdownListCache,
    dropdownHasMore,
    projectsRefreshKey,
    replaceDropdownListCache,
    cachedSelectedProject,
    setCachedSelectedProject,
    deletedProjectIds,
  } = useProjectContext();

  const {
    items,
    loading,
    loadInFlightRef,
    loadPage,
    pageSize,
    hasMore,
    mergeProjects,
  } = useProjectDropdownOptions({
    seedProjects: dropdownListCache,
    seedHasMore: dropdownHasMore,
    persistSelectionProjectId: selectedProjectId,
  });

  // Cache only unfiltered lists so route changes rehydrate the full page,
  // never stale search results.
  useEffect(() => {
    if (items.length > 0 && !debouncedFilter) {
      replaceDropdownListCache(items, hasMore);
    }
  }, [items, hasMore, debouncedFilter, replaceDropdownListCache]);

  /**
   * Reload page 1 when a project is created or deleted.
   *
   * Clearing the context cache is not enough on its own: a mounted dropdown
   * holds its own `items`, so it would go on showing the list it already had —
   * including a project that no longer exists. The prefetch below runs once per
   * mount and cannot cover this.
   */
  const lastRefreshKeyRef = useRef(projectsRefreshKey);
  useEffect(() => {
    if (lastRefreshKeyRef.current === projectsRefreshKey) return;
    lastRefreshKeyRef.current = projectsRefreshKey;
    prevDebouncedFilterRef.current = debouncedFilter;
    firstPageRequestedRef.current = true;
    void loadPage(debouncedFilter, 0, pageSize);
  }, [projectsRefreshKey, debouncedFilter, loadPage, pageSize]);

  // Prefetch first page on mount only when context has no cached list.
  useEffect(() => {
    if (didMountPrefetchRef.current) return;
    didMountPrefetchRef.current = true;
    if (dropdownListCache.length > 0) return;
    prevDebouncedFilterRef.current = '';
    firstPageRequestedRef.current = true;
    void loadPage('', 0, pageSize);
  }, [dropdownListCache.length, loadPage, pageSize]);

  useEnsureSelectedProjectInList({ items, mergeProjects, pageSize });

  /**
   * Options minus anything this session deleted.
   *
   * The server keeps listing a deleted project after its 204, so it comes back
   * in the very next page-1 fetch. Offering it would hand the reader a project
   * that is gone and an id that no longer resolves.
   */
  const options = useMemo(
    () =>
      deletedProjectIds.length === 0
        ? items
        : items.filter(
            (project) => !deletedProjectIds.includes(project.projectId),
          ),
    [items, deletedProjectIds],
  );

  const selected = useMemo(() => {
    if (!selectedProjectId) return null;
    return (
      items.find((p) => p.projectId === selectedProjectId) ??
      (cachedSelectedProject?.projectId === selectedProjectId
        ? cachedSelectedProject
        : null)
    );
  }, [selectedProjectId, items, cachedSelectedProject]);

  useEffect(() => {
    if (!hasOpenedRef.current) return;
    if (prevDebouncedFilterRef.current === debouncedFilter) return;
    prevDebouncedFilterRef.current = debouncedFilter;
    loadPage(debouncedFilter, 0, pageSize);
  }, [debouncedFilter, pageSize, loadPage]);

  const handleShow = useCallback(() => {
    if (hasOpenedRef.current) return;
    hasOpenedRef.current = true;
    prevDebouncedFilterRef.current = debouncedFilter;
    firstPageRequestedRef.current = true;
    if (items.length === 0) {
      loadPage('', 0, pageSize);
    }
  }, [loadPage, pageSize, items.length, debouncedFilter]);

  const handleLazyLoad = useCallback(
    (e: { first: number; last: number }) => {
      if (loadInFlightRef.current || !hasMore) return;
      const lastIdx = e.last ?? -1;
      if (lastIdx < items.length) return;
      if (items.length === 0) {
        if (firstPageRequestedRef.current) return;
        firstPageRequestedRef.current = true;
      }
      loadPage(debouncedFilter, items.length, pageSize);
    },
    [debouncedFilter, loadPage, hasMore, items.length, pageSize, loadInFlightRef]
  );

  const handleSelect = useCallback(
    (project: Project | null) => {
      if (!project) {
        setCachedSelectedProject(null);
        setSelectedProjectId(null);
        setProjectSelectorVisible(true);
        return;
      }
      setCachedSelectedProject(project);
      setSelectedProjectId(project.projectId);
    },
    [setCachedSelectedProject, setSelectedProjectId, setProjectSelectorVisible]
  );

  const isSmall = size === 'small';
  const showLoadingOnTrigger = loading && items.length > 0;

  const valueTemplate = useCallback(
    (option: Project | null) => {
      if (!option) {
        return (
          <span
            className={
              isSmall ? 'text-sm text-foreground dark:text-foreground-dark' : 'text-foreground dark:text-foreground-dark'
            }
          >
            Select a project
          </span>
        );
      }
      return (
        <span className={isSmall ? 'text-sm' : undefined}>
          {option.projectName}
        </span>
      );
    },
    [isSmall]
  );

  const itemTemplate = useCallback(
    (option: Project) => (
      <div
        className={`text-foreground dark:text-foreground-dark hover:bg-accent dark:hover:bg-selected-item-dark ${
          isSmall ? 'px-3 py-2 text-sm' : 'p-5'
        }`}
      >
        {option.projectName}
      </div>
    ),
    [isSmall]
  );

  const ptOverrides = useMemo(
    () => ({
      item: { className: 'w-full !p-0' },
      itemLabel: { className: 'w-full' },
      panel: {
        className: 'bg-card text-card-foreground border-border',
      },
      input: {
        className: `bg-input text-foreground border-border${
          isSmall ? ' text-sm' : ''
        }`,
      },
    }),
    [isSmall]
  );

  const virtualScrollerOptions = useMemo(
    () => ({
      itemSize: isSmall ? 38 : 52,
      lazy: true,
      onLazyLoad: handleLazyLoad,
    }),
    [isSmall, handleLazyLoad]
  );

  return (
    <Dropdown
      value={selected}
      dataKey='projectId'
      draggable={false}
      onChange={(e) => handleSelect(e.value ?? null)}
      options={options}
      optionLabel='projectName'
      placeholder='Select a project'
      ariaLabel={ariaLabel}
      valueTemplate={valueTemplate}
      className={isSmall ? 'w-52' : 'w-full'}
      loading={showLoadingOnTrigger}
      showClear={showClear}
      filter
      onFilter={(e) => setFilterInput(e.filter ?? '')}
      onShow={handleShow}
      virtualScrollerOptions={virtualScrollerOptions}
      pt={ptOverrides}
      itemTemplate={itemTemplate}
    />
  );
};
